import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export interface CartItem {
  eventId: string;
  eventTitle: string;
  racerName: string;
  racerClass: string;
  entryFeeCents: number;
}

interface CartContextValue {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (index: number) => void;
  clearEvent: (eventId: string) => void;
  clear: () => void;
}

const CartCtx = createContext<CartContextValue | null>(null);

const STORAGE_KEY = "rcracehq_cart";

function loadInitial(): CartItem[] {
  // Per-viewer convenience only (so a cart survives a page reload mid
  // session) -- never relied on as a source of truth; checkout always goes
  // through POST /checkout, which re-validates everything server side.
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

function persist(items: CartItem[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore (private mode / storage disabled)
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(() => loadInitial());

  const addItem = useCallback((item: CartItem) => {
    setItems((prev) => {
      const next = [...prev, item];
      persist(next);
      return next;
    });
  }, []);

  const removeItem = useCallback((index: number) => {
    setItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      persist(next);
      return next;
    });
  }, []);

  const clearEvent = useCallback((eventId: string) => {
    setItems((prev) => {
      const next = prev.filter((i) => i.eventId !== eventId);
      persist(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    persist([]);
  }, []);

  const value = useMemo<CartContextValue>(
    () => ({ items, addItem, removeItem, clearEvent, clear }),
    [items, addItem, removeItem, clearEvent, clear],
  );

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
